import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid } from "@mui/material";
import { MouseEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

import { InvoiceLineItems } from "../../interfaces/Gig";
import { theme } from "../../mui-customs/Theme";
import Button from "../Button";
import TextField from "../TextField";
import Typography from "../Typography";

const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  rate: yup
    .number()
    .test("is-decimal", "Maximum two digits after comma", (val: any) => {
      if (val != undefined) {
        return /^\d+(\.\d{0,2})?$/.test(val);
      }
      return true;
    })
    .required("Rate is required")
    .typeError("Rate is required"),
  quantity: yup
    .number()
    .min(1, "Enter a valid quantity")
    .integer("Enter a valid quantity")
    .required("Quantity is required")
    .typeError("Quantity is required"),
});

type FormValues = {
  title: string;
  rate: string;
  quantity: string;
};

interface AddAndEditInvoiceLineItemProps {
  enableDisplayMode?: () => void;
  invoiceData?: InvoiceLineItems;
  invoiceSubmitHandler?: (data: InvoiceLineItems) => void;
  handleInvoiceEdit?: (data: InvoiceLineItems) => void;
}

const AddAndEditInvoiceLineItem = ({
  enableDisplayMode,
  invoiceData,
  invoiceSubmitHandler,
  handleInvoiceEdit,
}: AddAndEditInvoiceLineItemProps) => {
  const [total, setTotal] = useState<number>(0);
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...invoiceData,
      rate: `${invoiceData?.rate}`,
      quantity: `${invoiceData?.quantity}`,
    },
    mode: "onChange",
  });
  const handleCalculateTotal = () => {
    const data = getValues();
    if (data.rate === "undefined" || data.quantity === "undefined") {
      return;
    }
    const newTotal =
      Math.round(parseFloat(data.rate) * parseInt(data.quantity, 10) * 100) /
      100;
    setTotal(newTotal);
  };
  const handleSubmitForm = (data: FormValues) => {
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the errors before submitting");
      return;
    }
    const newData: InvoiceLineItems = {
      title: data.title,
      rate: parseInt(data.rate, 10),
      quantity: parseInt(data.quantity, 10),
    };
    if (invoiceData) {
      enableDisplayMode && enableDisplayMode();
      handleInvoiceEdit && handleInvoiceEdit(newData);
    } else {
      invoiceSubmitHandler && invoiceSubmitHandler(newData);
      reset();
      setTotal(0);
    }
  };
  const submitForm = (e: MouseEvent) => {
    e.preventDefault();
    handleSubmit(handleSubmitForm)();
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.whiteBg.WBG2,
        borderRadius: 2,
        p: 2,
        position: "relative",
      }}
    >
      <form>
        <Box
          sx={{
            input: {
              padding: "7px !important",
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    placeholder="Example: hourly chef rate"
                    label="Line Item Description"
                    isRequired
                    error={Boolean(errors.title)}
                    helperText={errors.title?.message}
                    {...field}
                    ref={null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} md={5}>
              <Controller
                name="rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    type={"number"}
                    label="Rate"
                    isRequired
                    error={Boolean(errors.rate)}
                    helperText={errors.rate?.message}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleCalculateTotal();
                    }}
                    ref={null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} md={5}>
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    type={"number"}
                    label="Quantity"
                    isRequired
                    error={Boolean(errors.quantity)}
                    helperText={errors.quantity?.message}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleCalculateTotal();
                    }}
                    ref={null}
                  />
                )}
              />
            </Grid>
            <Grid
              sx={{
                alignItems: "end",
                justifyContent: "start",
                pb: 1,
                display: "flex",
              }}
              item
              xs={4}
              md={2}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: theme.brand1[700],
                }}
              >
                <Typography variant="title1">Total</Typography>
                <Typography variant="title2">${total}</Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                sx={{ padding: "7px", maxWidth: "176px" }}
                type="submit"
                onClick={(e) => submitForm(e)}
              >
                {invoiceData ? "Save" : "Add"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  );
};

export default AddAndEditInvoiceLineItem;
